/* eslint-disable object-curly-newline */
import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { TimerRegular } from '@fluentui/react-icons';
import {
  TilesGridCellImage as CellImage,
  TilesGridCellDecorativeLabel as CellLabel,
  TilesGridCellCardWithOverlay,
  TilesGridCellSelectionDecorator,
  TilesGridCellDragDecorator,
} from '../../ads-studio-tile-grid';
import { TextWithHighlight } from '../../text-with-highlight';
import {
  Video,
  EncodingPlaceholderWithStyle,
  UnverifiedPlaceholderWithStyle,
  FailedPlaceholderWithStyle
} from '../../video-component';
import { convertTimeDuration } from '../../../model';

import { useVideoAssetsGridCellStyle } from './style';

export const AdsStudioVideoAssetsGridCell = ({
  item,
  assets,
  highlightedText,
  isOverlayEnabled,
  isCellFocused,
  renderCellContent,
  renderOverlayContent,
  selectable,
  selected,
  isMultiSelecting,
  onClick,
  i18n,
  onClickVideo,
  draggable,
  onDrag,
  selectedItemCount,
}) => {
  const classes = useVideoAssetsGridCellStyle();
  const [isVideoLoaded, setVideoLoaded] = useState(false);
  const [showVideo, setShowVideo] = useState(false); // control opacity of video element
  const [hideVideo, setHideVideo] = useState(false); // display video element or not
  const [thumbnailSeletable, setThumbnailSelectable] = useState(false);

  const { name, thumbnailUrl, id, durationInMS, status, url } = item || {};
  const durationDesc = useMemo(() => convertTimeDuration(durationInMS), [durationInMS]);

  const [cellRef, setCellRef] = useState(null);
  const timerRef = useRef(null);
  const currentStatus = useRef(status);

  useEffect(() => {
    if (assets && status === 'Active' && currentStatus.current !== 'Active') {
      // update active asset info in assets array
      const targetIdx = assets.findIndex(asset => asset.id === id);
      if (targetIdx >= 0) {
        // eslint-disable-next-line no-param-reassign
        assets[targetIdx] = { ...item };
      }
    }
    currentStatus.current = status;
  }, [assets, id, item, status]);

  useEffect(() => {
    if (isVideoLoaded && cellRef && !isMultiSelecting) {
      cellRef.addEventListener('mouseover', () => {
        setShowVideo(true);
        timerRef.current = setTimeout(() => {
          setShowVideo(false);
          setThumbnailSelectable(true);
          setHideVideo(true);
        }, 5000);
      });
      cellRef.addEventListener('mouseout', () => {
        setShowVideo(false);
        clearTimeout(timerRef.current);
        setThumbnailSelectable(false);
      });
    }
  }, [cellRef, isMultiSelecting, isVideoLoaded]);

  useEffect(() => () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  }, []);

  useEffect(() => {
    if (selected) {
      setShowVideo(false);
      setThumbnailSelectable(true);
    }
  }, [selected]);

  const onClickVideoCard = useCallback(() => {
    clearTimeout(timerRef.current);
    onClickVideo();
  }, [onClickVideo]);

  const playVideoObject = useMemo(() => ({ play: showVideo }), [showVideo]);

  const wrapperWidth = cellRef ? cellRef.offsetWidth : 0;

  const cellContent = useMemo(() => {
    let defaultContent = null;
    // status order: unverified => draft => active/invalid
    if (status === 'Active') {
      defaultContent = (
        <div className="acitve-video-cell-content">
          <CellImage
            src={thumbnailUrl}
            alt={name || id || ''}
            showSpinner={false}
            onLoaded={setVideoLoaded}
            i18n={i18n}
          />
          <div className={classes.videoCellLabelContainer}>
            {isVideoLoaded && durationDesc && !showVideo && (
              <CellLabel
                text={durationDesc}
                icon={TimerRegular}
              />
            )}
          </div>
        </div>
      );
    } else {
      setVideoLoaded(true);

      if (status === 'Unverified') {
        defaultContent = <div className={classnames('unverified-video-cell-content', classes.placeholderCell)}><UnverifiedPlaceholderWithStyle placeholderContent={i18n.getString(_TL_('Downloading...'))} /></div>;
      } else if (status === 'Draft') {
        defaultContent = <div className={classnames('draft-video-cell-content', classes.placeholderCell)}><EncodingPlaceholderWithStyle placeholderContent={i18n.getString(_TL_('Processing...'))} /></div>;
      } else if (status === 'Failed') {
        defaultContent = <div className={classnames('failed-video-cell-content', classes.placeholderCell)}><FailedPlaceholderWithStyle placeholderContent={i18n.getString(_TL_('Invalid video'))} /></div>;
      }
    }

    if (renderCellContent) {
      return renderCellContent(defaultContent);
    }
    return defaultContent;
  }, [status, renderCellContent, thumbnailUrl, name, id, i18n, classes.videoCellLabelContainer, classes.placeholderCell, isVideoLoaded, showVideo, durationDesc]);

  const funcRenderOverlayContent = useCallback(() => {
    if (renderOverlayContent) {
      return renderOverlayContent();
    }
    return null;
  }, [renderOverlayContent]);

  const onCellClick = useCallback(() => {
    if (selectable && thumbnailSeletable) {
      onClick({ item, selected });
    } else if (showVideo) {
      onClickVideoCard();
    }
  }, [item, onClick, onClickVideoCard, selectable, selected, showVideo, thumbnailSeletable]);

  useEffect(() => {
    if (isMultiSelecting) {
      setThumbnailSelectable(true);
    }
  }, [isMultiSelecting]);

  return (
    <div className={classnames('tiles-grid-cell-content', classes.VideoAssetsGridCellContainer)}>
      <TilesGridCellDragDecorator
        draggable={draggable}
        item={item}
        dragItemsLength={selectedItemCount}
        onDrag={onDrag}
        i18n={i18n}
        selected={selected}
      >
        <TilesGridCellCardWithOverlay
          isCellFocused={isCellFocused}
          isOverlayEnabled={isOverlayEnabled && isVideoLoaded && thumbnailSeletable}
          renderOverlayContent={funcRenderOverlayContent}
          customCellCardClass={(isOverlayEnabled && !selected && !isMultiSelecting) ? classes.selectableCardContainerAutoHideCheckbox : classes.selectableCardContainer}
          onClick={onCellClick}
        >
          {selectable && thumbnailSeletable ? (
            <TilesGridCellSelectionDecorator item={item} selected={selected} onClick={onClick} ariaLabel={name || i18n.getString(_TL_('Video'))} isFocused={isCellFocused} >
              {cellContent}
            </TilesGridCellSelectionDecorator>
            ) : cellContent}
        </TilesGridCellCardWithOverlay>
        {
          !hideVideo && !isMultiSelecting && status === 'Active' &&
          <div key={id} className={classnames('video-wrapper', classes.videoWrapper)} ref={x => setCellRef(x)}>
            <Video
              src={url}
              autoPlay={false}
              muteByDefault
              i18n={i18n}
              ariaLabel={i18n.getString(_TL_('Video'))}
              maxWidth={wrapperWidth}
              showTime
              className={classnames(classes.videoCell, isVideoLoaded && showVideo && 'show-video')}
              showPlay={false}
              customPlayObject={playVideoObject}
            />
          </div>
        }
      </TilesGridCellDragDecorator>
      <div className="cell-description">
        <div className="cell-description-name">
          <TextWithHighlight text={name || ''} highlightedText={highlightedText} enableTooltip />
        </div>
        {id && (
          <div className="cell-description-id">
            {`${i18n.getString(_TL_('ID'))} `}
            <TextWithHighlight text={id} highlightedText={highlightedText} />
          </div>
        )}
      </div>
    </div>
  );
};

AdsStudioVideoAssetsGridCell.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
    createdTime: PropTypes.instanceOf(Date),
  }).isRequired,
  assets: PropTypes.arrayOf({ id: PropTypes.number }),
  highlightedText: PropTypes.string,
  isOverlayEnabled: PropTypes.bool,
  isCellFocused: PropTypes.bool,
  renderCellContent: PropTypes.func,
  renderOverlayContent: PropTypes.func,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  onClick: () => null,
  isMultiSelecting: PropTypes.bool,
  onClickVideo: PropTypes.func.isRequired,
};

AdsStudioVideoAssetsGridCell.defaultProps = {
  highlightedText: undefined,
  isOverlayEnabled: false,
  isCellFocused: false,
  renderCellContent: null,
  renderOverlayContent: null,
  selectable: false,
  selected: false,
  onClick: undefined,
  isMultiSelecting: false,
  assets: [],
};
